// updateGuard.js — "safe moment" signals + state restoration for the
// seamless auto-update mechanism.
// ===========================================================================
// The SW lifecycle (register / version-gate / phantom-suppression / reload)
// lives in public/index.html. That controller decides WHEN to apply a waiting
// update by reading a few plain globals this module maintains:
//
//   window.__updateGuards = { inFlightWrites, dirtyForms, openModals, lastWriteEndedAt }
//   window.__captureRestoreState = () => { /* snapshot scroll/modals/drafts */ }
//
// The controller only reloads when (tab hidden || idle) AND inFlightWrites===0
// AND no dirty form / open modal — so an auto-update never nukes a save in
// flight or unsaved work, and the user lands back exactly where they were.
//
// Everything here is defensive: if React never mounts (e.g. a standalone
// /3c-*.html page) the globals simply stay at their safe defaults and the
// controller treats the app as clean.
// ===========================================================================
import { useEffect, useRef } from 'react';

const GUARDS = (typeof window !== 'undefined' && window.__updateGuards) || {
  inFlightWrites: 0,
  dirtyForms: 0,
  openModals: 0,
  lastWriteEndedAt: 0,
};
if (typeof window !== 'undefined') window.__updateGuards = GUARDS;

// ---- in-flight write tracking ---------------------------------------------
// Every data MUTATION (not read) funnels through here so the update controller
// can refuse to reload mid-write. Wired centrally at the api layer:
//   - apiLegacy.post()  → begin/endWrite (covers every /exec write)
//   - api.js            → trackWrite() wraps each migrated Supabase mutation
export function beginWrite() {
  GUARDS.inFlightWrites += 1;
}
export function endWrite() {
  GUARDS.inFlightWrites = Math.max(0, GUARDS.inFlightWrites - 1);
  GUARDS.lastWriteEndedAt = Date.now();
}
// Wrap a mutation promise so the counter brackets it regardless of outcome.
export function trackWrite(promise) {
  beginWrite();
  return Promise.resolve(promise).finally(endWrite);
}

// ---- dirty-form + open-modal registries -----------------------------------
// Explicit opt-in (not a DOM heuristic): search boxes / autocompletes would
// false-positive forever on an always-on screen and permanently block updates.
let _dirtyId = 0;
const _dirtySet = new Set();
const _modalSet = new Set();

function _sync() {
  GUARDS.dirtyForms = _dirtySet.size;
  GUARDS.openModals = _modalSet.size;
}

// React hook: register this component as "has unsaved input" while `isDirty`.
// `draftKey`/`draft` (optional) persist the in-progress values to sessionStorage
// so even a forced reload can rehydrate them (see readDraft/clearDraft).
export function useDirtyGuard(isDirty, draftKey, draft) {
  const idRef = useRef(null);
  if (idRef.current == null) idRef.current = 'd' + (++_dirtyId);
  useEffect(() => {
    const id = idRef.current;
    if (isDirty) {
      _dirtySet.add(id);
      if (draftKey) {
        try { sessionStorage.setItem(draftKey, JSON.stringify(draft)); } catch (_) {}
      }
    } else {
      _dirtySet.delete(id);
      if (draftKey) { try { sessionStorage.removeItem(draftKey); } catch (_) {} }
    }
    _sync();
    return () => { _dirtySet.delete(idRef.current); _sync(); };
  }, [isDirty, draftKey, draft]);
}

// React hook: register an open modal so an auto-reload waits for it to close.
// No "re-open after reload" needed — the controller never reloads while a modal
// is open, so the modal simply survives by virtue of the page not reloading.
export function useModalGuard(isOpen, id) {
  useEffect(() => {
    if (!isOpen) return undefined;
    _modalSet.add(id);
    _sync();
    return () => { _modalSet.delete(id); _sync(); };
  }, [isOpen, id]);
}

// ---- draft snapshot helpers (used by guarded form sites) ------------------
// Read a persisted draft once on mount; returns undefined if none/!valid.
export function readDraft(draftKey) {
  try {
    const raw = sessionStorage.getItem(draftKey);
    return raw ? JSON.parse(raw) : undefined;
  } catch (_) { return undefined; }
}
export function clearDraft(draftKey) {
  try { sessionStorage.removeItem(draftKey); } catch (_) {}
}

// ---- scroll capture / restore ---------------------------------------------
// The page itself never scrolls (#root is height:100%); the real scrollers are
// the per-app containers. Capture each by a stable key and restore after the
// new bundle mounts. Tabs mount lazily after activeTab restores, so restore
// retries across a few animation frames.
const SCROLL_KEY = '3c_scroll';
const SCROLL_SELECTOR = '.dash-main, .det-main, [data-scroll-key]';

function _scrollKeyFor(el) {
  return el.getAttribute('data-scroll-key') || el.className.split(/\s+/)[0] || el.tagName.toLowerCase();
}

export function captureScroll() {
  try {
    const map = {};
    document.querySelectorAll(SCROLL_SELECTOR).forEach((el) => {
      if (el.scrollTop > 0) map[_scrollKeyFor(el)] = el.scrollTop;
    });
    sessionStorage.setItem(SCROLL_KEY, JSON.stringify(map));
  } catch (_) {}
}

export function restoreScroll() {
  let map;
  try {
    map = JSON.parse(sessionStorage.getItem(SCROLL_KEY) || 'null');
  } catch (_) { map = null; }
  if (!map || !Object.keys(map).length) { try { sessionStorage.removeItem(SCROLL_KEY); } catch (_) {} return; }
  // Retry across frames: the scroll container mounts only after auth resolves
  // and the active tab (restored from its own sessionStorage key) renders. Cap
  // ~1s so a key whose container never reappears doesn't spin forever.
  let frames = 0;
  const tryRestore = () => {
    let allFound = true;
    Object.keys(map).forEach((key) => {
      const el = document.querySelector('[data-scroll-key="' + key + '"]') || document.querySelector('.' + key);
      if (el) el.scrollTop = map[key];
      else allFound = false;
    });
    frames += 1;
    if (allFound || frames >= 60) { try { sessionStorage.removeItem(SCROLL_KEY); } catch (_) {} return; }
    requestAnimationFrame(tryRestore);
  };
  requestAnimationFrame(tryRestore);
}

// Called by the HTML controller immediately before it applies an update.
// Snapshots scroll now; dirty-form drafts + open-modal ids are already kept
// live in sessionStorage by their hooks, so there's nothing else to flush.
export function captureRestoreState() {
  captureScroll();
}

// Install the bridge the moment this module loads (it's imported from App.jsx).
if (typeof window !== 'undefined') {
  window.__captureRestoreState = captureRestoreState;
}
